/**
 * SlickGrid Filter Utility Functions
 *
 * Pure filter functions that can be tested independently without DOM dependencies.
 * These are used by both the SlickGrid component and unit tests.
 */

import dayjs, { type Dayjs } from "dayjs";
import axiosWrapper from "@Services/axiosWrapper";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type QueryOperator =
  | "startswith"
  | "endswith"
  | "empty"
  | "equal"
  | "contains"
  | "like"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "notequal"
  | "notempty"
  | "notnull"
  | "null"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "between";

export type FilterType = "Text" | "Date" | "Number" | "MultiSelect" | "FileSize";

export interface DateFilterValue {
  date?: Dayjs;
  startDate?: Dayjs;
  endDate?: Dayjs;
}

const isDateFilterValue = (value: unknown): value is DateFilterValue => typeof value === "object" && value !== null;

export interface MultiSelectItem {
  name: string;
  [key: string]: unknown;
}

export interface FileSizeValue {
  size: string | number;
  unit: string;
}

const toFilterDate = (value: unknown): Dayjs => {
  if (dayjs.isDayjs(value)) return value;
  if (isDateFilterValue(value) && "date" in value) return toFilterDate(value.date);
  if (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    value instanceof Date
  ) {
    return dayjs(value);
  }
  // Nothing dayjs could parse (a range wrapper, a boolean, an array): an Invalid Date, which every
  // comparison below rejects — same outcome as handing the raw value to dayjs().
  return dayjs(NaN);
};

const toRangeBounds = (value: unknown): { start: Dayjs; end: Dayjs } => {
  const bound = (edge: unknown): Dayjs => (edge === null || edge === undefined ? dayjs(NaN) : toFilterDate(edge));
  if (!isDateFilterValue(value)) return { start: dayjs(NaN), end: dayjs(NaN) };
  return { start: bound(value.startDate), end: bound(value.endDate) };
};

// ============================================================================
// DATE FILTER
// ============================================================================

export const applyDateFilter = (
  fieldValue: unknown,
  filterOption: QueryOperator,
  filterValue: DateFilterValue | Dayjs,
): boolean => {
  const format = "MMM D,YYYY [at] hh:mm:ss A";
  const fieldStr = String(fieldValue ?? "");
  const itemDate = Number.isNaN(Number(fieldValue)) ? dayjs(fieldStr, format) : dayjs(Number(fieldValue));

  // Check if the field value is null/undefined/empty BEFORE comparing
  const isNullOrEmpty = !fieldValue || fieldStr.trim() === "";

  const filterDate = toFilterDate(filterValue);

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values (they should not match any date comparison)
    equal: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isSame(filterDate, "day"),
    greaterthan: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isAfter(filterDate, "day"),
    greaterthanorequal: () =>
      !isNullOrEmpty &&
      itemDate.isValid() &&
      (itemDate.isSame(filterDate, "day") || itemDate.isAfter(filterDate, "day")),
    lessthan: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isBefore(filterDate, "day"),
    lessthanorequal: () =>
      !isNullOrEmpty &&
      itemDate.isValid() &&
      (itemDate.isSame(filterDate, "day") || itemDate.isBefore(filterDate, "day")),
    notequal: () => !isNullOrEmpty && itemDate.isValid() && !itemDate.isSame(filterDate, "day"),
    between: () => {
      const { start, end } = toRangeBounds(filterValue);
      const afterStart = !start.isValid() || applyDateFilter(fieldValue, "greaterthanorequal", start);
      const beforeEnd = !end.isValid() || applyDateFilter(fieldValue, "lessthanorequal", end);
      return afterStart && beforeEnd;
    },
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
  };

  return filters[filterOption]?.() ?? false;
};

// ============================================================================
// TEXT FILTER
// ============================================================================

export const applyTextFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
  const fieldValueStr = String(fieldValue ?? "").toLowerCase();
  const filterValueLower = String(filterValue ?? "").toLowerCase();

  // Check if original value is actually null (not just empty string)
  const isNull = fieldValue === null || fieldValue === undefined;

  const filters: Record<string, () => boolean> = {
    startswith: () => fieldValueStr.startsWith(filterValueLower),
    endswith: () => fieldValueStr.endsWith(filterValueLower),
    empty: () => fieldValueStr === "",
    equal: () => fieldValueStr === filterValueLower,
    contains: () => fieldValueStr.includes(filterValueLower),
    like: () => fieldValueStr.includes(filterValueLower),
    doesnotstartwith: () => !fieldValueStr.startsWith(filterValueLower),
    doesnotendwith: () => !fieldValueStr.endsWith(filterValueLower),
    doesnotcontain: () => !fieldValueStr.includes(filterValueLower),
    notequal: () => fieldValueStr !== filterValueLower,
    notempty: () => fieldValueStr !== "",
    null: () => isNull,
    notnull: () => !isNull,
  };

  return filters[filterOption]?.() ?? false;
};

// ============================================================================
// NUMBER FILTER
// ============================================================================

export const applyNumberFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
  const fieldValueAsNumber = Number(fieldValue);
  const filterValueAsNumber = Number(filterValue);

  // Check if the original value is actually null/undefined/empty (not just converts to 0)
  const isNullOrEmpty =
    fieldValue === null ||
    fieldValue === undefined ||
    fieldValue === "" ||
    (typeof fieldValue === "string" && fieldValue.trim() === "") ||
    Number.isNaN(fieldValueAsNumber);

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values (they should not match numeric comparisons)
    equal: () => !isNullOrEmpty && fieldValueAsNumber === filterValueAsNumber,
    greaterthan: () => !isNullOrEmpty && fieldValueAsNumber > filterValueAsNumber,
    greaterthanorequal: () => !isNullOrEmpty && fieldValueAsNumber >= filterValueAsNumber,
    lessthan: () => !isNullOrEmpty && fieldValueAsNumber < filterValueAsNumber,
    lessthanorequal: () => !isNullOrEmpty && fieldValueAsNumber <= filterValueAsNumber,
    notequal: () => !isNullOrEmpty && fieldValueAsNumber !== filterValueAsNumber,
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
    startswith: () => true,
  };

  return filters[filterOption]?.() ?? false;
};

// ============================================================================
// MULTISELECT FILTER
// ============================================================================

export const applyMultiSelectFilter = (fieldValue: unknown, filterValue: unknown): boolean => {
  const selectedValues: unknown[] = Array.isArray(filterValue) ? filterValue : [filterValue];

  if (Array.isArray(fieldValue)) {
    const fieldValues = (fieldValue as MultiSelectItem[]).map((item) =>
      typeof item === "object" && item !== null && "name" in item ? item.name : String(item),
    );
    return selectedValues.some((selectedValue) =>
      fieldValues.some((fieldVal) => String(fieldVal).toLowerCase() === String(selectedValue).toLowerCase()),
    );
  }

  return selectedValues.some(
    (selectedValue) => String(fieldValue).toLowerCase() === String(selectedValue).toLowerCase(),
  );
};

// ============================================================================
// FILE SIZE FILTER
// ============================================================================

/** Convert file size with unit to bytes */
export const convertFileSizeToBytes = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (!value || typeof value !== "object") return 0;

  const sizeValue = value as { size?: string | number; unit?: string };
  const size = Number(sizeValue.size) || 0;
  const unit = (sizeValue.unit || "B").toUpperCase();

  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };

  return size * (multipliers[unit] || 1);
};

export const applyFileSizeFilter = (
  fieldValue: unknown,
  filterOption: QueryOperator,
  filterValue: unknown,
): boolean => {
  const fieldValueAsNumber = Number(fieldValue);
  const filterValueAsNumber = convertFileSizeToBytes(filterValue);

  // Check if the original value is null/undefined (NaN when converted)
  const isNullOrEmpty = fieldValue === null || fieldValue === undefined || Number.isNaN(fieldValueAsNumber);

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values
    equal: () => !isNullOrEmpty && fieldValueAsNumber === filterValueAsNumber,
    greaterthan: () => !isNullOrEmpty && fieldValueAsNumber > filterValueAsNumber,
    greaterthanorequal: () => !isNullOrEmpty && fieldValueAsNumber >= filterValueAsNumber,
    lessthan: () => !isNullOrEmpty && fieldValueAsNumber < filterValueAsNumber,
    lessthanorequal: () => !isNullOrEmpty && fieldValueAsNumber <= filterValueAsNumber,
    notequal: () => !isNullOrEmpty && fieldValueAsNumber !== filterValueAsNumber,
    // null operator: true for null/undefined/NaN values (0 is a valid file size)
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
  };

  return filters[filterOption]?.() ?? false;
};

// ============================================================================
// COMBINED FILTER LOGIC
// ============================================================================

export const getConditionMet = (
  filterType: FilterType,
  fieldValue: unknown,
  filterOption: QueryOperator,
  conditionText: unknown,
): boolean => {
  const filterMap: Record<string, () => boolean> = {
    Date: () => applyDateFilter(fieldValue, filterOption, conditionText as DateFilterValue),
    Text: () => applyTextFilter(fieldValue, filterOption, conditionText),
    Number: () => applyNumberFilter(fieldValue, filterOption, conditionText),
    MultiSelect: () => applyMultiSelectFilter(fieldValue, conditionText),
    FileSize: () => applyFileSizeFilter(fieldValue, filterOption, conditionText),
  };

  return filterMap[filterType]?.() ?? false;
};

// ============================================================================
// SHARED FILTER HELPERS (extracted from SlickGrid1/index.tsx for reuse)
// ============================================================================

/** Combine a running filter result with a new condition using AND/OR logic */
export const applyLogicToResult = (currentResult: boolean, conditionMet: boolean, logic: string): boolean => {
  return logic === "or" ? currentResult || conditionMet : currentResult && conditionMet;
};

/**
 * Generic remote call helper — calls a URL string via axiosWrapper.post or invokes
 * a function directly. Extracted from SlickGrid1's callRemote for reuse.
 */
export const callRemoteSource = async <TBody, TResult>(
  source: string | ((body: TBody) => Promise<TResult>),
  body: TBody,
): Promise<TResult> => {
  if (typeof source === "function") {
    return await source(body);
  }
  return (await axiosWrapper.post(source, body)) as TResult;
};

/**
 * Walk up the parent chain from each matched ID and collect all ancestor IDs.
 * Returns a Set containing the matched IDs + all ancestor IDs needed for tree display.
 *
 * Extracted from SlickGrid1's collectMissingParents / Gantt's ancestor walk.
 *
 * @param matchedIds  - IDs of items that passed the filter
 * @param getParentId - given an ID, return its parent ID (or null/undefined if root)
 */
export function collectAncestorIds<TId>(matchedIds: TId[], getParentId: (id: TId) => TId | null | undefined): Set<TId> {
  const resultSet = new Set<TId>(matchedIds);

  for (const id of matchedIds) {
    let parentId = getParentId(id);
    while (parentId != null) {
      if (resultSet.has(parentId)) break;
      resultSet.add(parentId);
      parentId = getParentId(parentId);
    }
  }

  return resultSet;
}
